/**
 * The one-off Bolt Star onboarding dialog, shown once per browser to merchants in the programme.
 *
 * It lives in the design-system folder rather than next to its container in `app/src/widgets/`
 * to be lazy and not deal with tailwind setup. Move this folder to `app/src/widgets/` once the
 * Clay migration is finished and tailwind works across the Portal.
 */
import React, { useCallback } from 'react';

import { Button, Dialog, IconButton } from '@bolteu/clay-react';
import { Cross } from '@bolteu/kalep-react-icons';

import illustrationSrc from '../assets/bolt-star-illustration.png';

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export interface BoltStarOnboardingDialogProps {
  isOpen: boolean;
  /** Fired only by the close button: the backdrop and Escape deliberately do not dismiss. */
  onClose: () => void;
  onAction: () => void;
  title: string;
  description: string;
  actionLabel: string;
  closeLabel: string;
  illustrationAlt: string;
}

export function BoltStarOnboardingDialog({
  isOpen,
  onClose,
  onAction,
  title,
  description,
  actionLabel,
  closeLabel,
  illustrationAlt,
}: BoltStarOnboardingDialogProps) {
  const handleOpenChange = useCallback(
    (open: boolean, eventDetails: DialogChangeEventDetails) => {
      // block closing via shroud or ESC
      if (!open && eventDetails.reason === 'close-press') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-100 shadow-lg">
          <Dialog.Header className="shrink-0 pb-3 pl-6 pr-4 pt-4">
            <Dialog.Close
              render={<IconButton aria-label={closeLabel} icon={<Cross />} size="sm" />}
              className="ml-auto"
            />
          </Dialog.Header>
          <Dialog.Description className="overflow-x-hidden -mt-4">
            <img src={illustrationSrc} alt={illustrationAlt} className="block w-full shrink-0" />
            {/* eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component */}
            <Dialog.Title className="bolt-font-heading-xs-accent" render={<h2 />}>
              {title}
            </Dialog.Title>
            <span>{description}</span>
          </Dialog.Description>
          <Dialog.Footer>
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {actionLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
